import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@material-tailwind/react';
import clsx from 'clsx';
import { MARINE_PHOTO_LIST } from '@constants';
import { ReactComponent as PrimaryCheck } from '@assets/icons/check_activity_success_icon.svg';
import DocumentSellerIcon from '@assets/icons/DocumentSellerIcon';
import { useNavigate } from 'react-router-dom';
import { useSellerStore } from '@stores';
import {
  CardCaptureStepEnum,
  DocumentCaptureStepEnum,
  LenderDocumentTypeEnum,
  VehiclePhotosEnum,
  cardCaptureType,
  AssetTypesEnum,
  MarinePhoneMappings
} from '@types';
import {
  ADD_INSPECTION_FILE,
  GET_INSPECTION_FILE,
  GET_INSPECTION_FILE_OCR,
  post
} from '@services';
import {
  processFile,
  type ProcessFileMetrics,
  FILE_VALIDATION_CONFIGS,
  validateFile,
  GeolocationManager,
  getExtensionFromBase64
} from '@utils';
import {
  areAllVehicleAssetPhotosDone,
  getCaptureGuidanceMessage,
  getMarinePhotoStepKey,
  getVehiclePhotoStepKey
} from '@utils/vehicleUtils';
import LoadingComponent from '@components/widgets/LoadingComponent';
import FileTypeErrorModal from '@components/widgets/FileTypeErrorModal';
import { UploadErrorModal } from './index';
import {
  recordAPIError,
  recordError,
  recordFileUploadEvent
} from '@utils/rumMonitoring';

interface CaptureResultProps {
  type: cardCaptureType;
  isUpload: boolean;
  image: string;
  uploadMetrics?: ProcessFileMetrics;
  setLoadImage: (image: any) => void;
  primaryColor: string;
  setStep: (step: number) => void;
}

let geolocationResult: any = null;
async function getGeolocationResult() {
  const result = await navigator.permissions.query({
    name: 'geolocation'
  });
  geolocationResult = result;
}
getGeolocationResult();

const CaptureResult: React.FC<CaptureResultProps> = ({
  type,
  image: initialImage,
  uploadMetrics,
  isUpload,
  primaryColor,
  setStep,
  setLoadImage
}) => {
  const { locationData, setShowLocationPrompt, inspectionConfig } =
    useSellerStore();
  const [trigger, setTrigger] = useState(false);
  const [showFileTypeError, setShowFileTypeError] = useState(false);
  const [fileErrorMessage, setFileErrorMessage] = useState('');

  const uploadTakeNewPicture = async () => {
    if (fileInputRef.current) {
      try {
        if (
          window.navigator.permissions ||
          inspectionConfig?.locationSharing === 'SILENT'
        ) {
          const result = geolocationResult;
          if (
            result.state === 'granted' ||
            inspectionConfig?.locationSharing === 'SILENT' ||
            (result.state === 'denied' &&
              inspectionConfig?.locationSharing === 'WARNING_PROMPT')
          ) {
            setShowLocationPrompt(false);
            fileInputRef.current?.click();
            return;
          }
        }

        setTrigger(true);
        setShowLocationPrompt(true);
      } catch (error) {
        recordError({ error, errorType: 'LOCATION_PERMISSION_ERROR' });
        console.error('Error checking location permission:', error);
        setTrigger(true);
        setShowLocationPrompt(true);
      }
    }
  };

  useEffect(() => {
    if (trigger && locationData) {
      setShowLocationPrompt(false);
      setTimeout(() => fileInputRef.current?.click(), 300);
      setTrigger(false);
    }
  }, [trigger, locationData]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validationConfig =
        type === DocumentCaptureStepEnum.bank
          ? FILE_VALIDATION_CONFIGS.BANK_STATEMENT
          : FILE_VALIDATION_CONFIGS.IMAGES_ONLY;

      const validationResult = validateFile(file, validationConfig);

      if (!validationResult.isValid) {
        setFileErrorMessage(
          validationResult.message || 'File validation failed'
        );
        setShowFileTypeError(true);
        event.target.value = '';
        return;
      }

      await processFile(file, async (imageDataUrl, compressedFile, metrics) => {
        // Store metrics for upload tracking
        if (metrics) {
          uploadMetricsRef.current = metrics;
        }
        setLoadImage(imageDataUrl);
        setBankImage({
          ...bank,
          fileName: compressedFile.name
        });
        setImage(imageDataUrl);
      });
    } else {
      console.log('No file selected.');
    }
  };

  const [isLoading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMetricsRef = useRef<ProcessFileMetrics | undefined>(
    uploadMetrics
  );
  const {
    vinObj,
    numberPlate,
    marineHin,
    marineRegoNumber,
    odometer,
    vehicle,
    bank,
    operatingHours,
    setOdometer,
    setOperatingHours,
    setVehicle,
    inspectionId,
    setVinFile,
    setPlateNumberFile,
    setCertFile,
    setOperatingHoursFile,
    setPayoutLetterFile,
    setOwnershipProofFile,
    setBankFile,
    setBankImage,
    setVinImage,
    setNumberPlateImage,
    setMarineHinImage,
    setMarineRegoNumberImage,
    vehicleFront,
    vehiclePassenger,
    vehicleDriver,
    vehicleBack,
    vehicleFrontSeats,
    vehicleBackSeats,
    vehicleBoot,
    vehicleLivingArea,
    vehicleKitchen,
    vehicleSleepingArea,
    setVehicleImageLivingArea,
    setVehicleImageKitchen,
    setVehicleImageSleepingArea,
    setVehicleImageFront,
    setVehicleImagePassenger,
    setVehicleImageDriver,
    setVehicleImageBack,
    setVehicleImageFrontSeats,
    setVehicleImageBackSeats,
    setVehicleImageBoot,
    setMarineImageFront,
    setMarineImageBack,
    setMarineImageLeft,
    setMarineImageRight,
    assetType
  } = useSellerStore();
  const navigator = useNavigate();
  const [geoLocation, setGeoLocation] = useState<{
    clientAccuracy?: number;
    clientLatitude?: number;
    clientLongitude?: number;
  }>({});
  const startBackgroundLocation = () => {
    const geolocationManager = GeolocationManager.getInstance();
    geolocationManager.startBackgroundLocationRequest();
  };
  const [isAddNew, setIsAddNew] = useState(true);
  const [targeFileId, setFileId] = useState<string | undefined>();

  useEffect(() => {
    startBackgroundLocation();
    const documentType =
      type === CardCaptureStepEnum.vehiclePhoto
        ? VehiclePhotosEnum[vehicle.vehiclePosition - 1]
        : type;

    const documentTypeFileMap: Record<string, any> = {
      [CardCaptureStepEnum.vin]: vinObj.file,
      [CardCaptureStepEnum.plateNumber]: numberPlate.file,
      [CardCaptureStepEnum.odometer]: odometer.file,
      [CardCaptureStepEnum.operatingHours]: operatingHours.file,
      [DocumentCaptureStepEnum.bank]: bank.file,
      'ASSET_FRONT': vehicle.fileFront,
      'ASSET_RIGHT': vehicle.fileDriver,
      'ASSET_BACK': vehicle.fileBack,
      'ASSET_LEFT': vehicle.filePassenger,
      'INTERNAL_FRONT': vehicleFrontSeats.file,
      'INTERNAL_BACK': vehicleBackSeats.file,
      'INTERNAL_BOOT': vehicleBoot.file,
      'INTERNAL_LIVING_AREA': vehicleLivingArea.file,
      'INTERNAL_KITCHEN': vehicleKitchen.file,
      'INTERNAL_SLEEPING_AREA': vehicleSleepingArea.file
    };

    const fileObj = documentTypeFileMap[documentType];
    let fileId;

    if (fileObj && fileObj.fileId !== '') {
      setIsAddNew(false);
      setFileId(fileObj.fileId);
      fileId = fileObj.fileId;
    }

    if (fileId && fileId !== '') {
      setLoading(true);
      post(
        GET_INSPECTION_FILE,
        { inspectionId: inspectionId, fileId: fileId },
        false
      )
        .then((data) => {
          setLoading(false);
          if (data && data.dataBase64) {
            if (image === '') {
              setImage(`data:image/jpeg;base64,${data.dataBase64}`);
              setLoadImage(`data:image/jpeg;base64,${data.dataBase64}`);
            }

            if (
              data.file &&
              data.file?.documentType === LenderDocumentTypeEnum.VIN
            ) {
              setVinFile(data.file);
            } else if (
              data.file &&
              data.file?.documentType === LenderDocumentTypeEnum.REGO_PLATE
            ) {
              setPlateNumberFile(data.file);
            } else if (
              data.file &&
              data.file?.documentType === LenderDocumentTypeEnum.OPERATING_HOURS
            ) {
              setOperatingHoursFile(data.file);
            } else if (
              data.file &&
              data.file?.documentType === LenderDocumentTypeEnum.BANK_STATEMENTS
            ) {
              setBankFile(data.file);
            } else if (
              (data.file &&
                data.file?.documentType ===
                  LenderDocumentTypeEnum.ROADWORTHY_CERT) ||
              (data.file &&
                data.file?.documentType === LenderDocumentTypeEnum.SAFETY_CERT)
            ) {
              setCertFile(data.file);
            } else if (
              data.file &&
              data.file?.documentType === LenderDocumentTypeEnum.PAYOUT_LETTER
            ) {
              setPayoutLetterFile(data.file);
            } else if (
              data.file &&
              data.file?.documentType === LenderDocumentTypeEnum.OWNERSHIP_PROOF
            ) {
              setOwnershipProofFile(data.file);
            }
          }
        })
        .catch((e) => {
          recordAPIError(e, e.response?.data);
          setLoading(false);
          console.error(e);
        });
    }
  }, []);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const handleRetry = () => {
    setShowErrorDialog(false);
    window.location.reload();
  };

  const handleUpload = async () => {
    const isTypeOfVinPlateMarineHinMarineRegoNumberBank = [
      CardCaptureStepEnum.vin,
      CardCaptureStepEnum.plateNumber,
      CardCaptureStepEnum.marineHin,
      CardCaptureStepEnum.marineRegoNumber,
      DocumentCaptureStepEnum.bank
    ].includes(type);
    if (isTypeOfVinPlateMarineHinMarineRegoNumberBank) {
      uploadFile(type, image, (fileId?: any) => {
        setLoading(true);
        post(
          GET_INSPECTION_FILE_OCR,
        {
          inspectionId: inspectionId,
          fileId:
            fileId && fileId !== ''
              ? fileId
              : CardCaptureStepEnum.vin === type
                ? vinObj.file?.fileId || ''
                : CardCaptureStepEnum.plateNumber === type
                  ? numberPlate.file?.fileId || ''
                  : CardCaptureStepEnum.marineHin === type
                    ? marineHin.file?.fileId || ''
                    : CardCaptureStepEnum.marineRegoNumber === type
                      ? marineRegoNumber.file?.fileId || ''
                      : bank.file?.fileId || ''
        },
        false
      )
        .then((data) => {
          if (
            !data ||
            !data.status ||
            (data.status !== 'COMPLETE' && data.status !== 'ERROR')
          ) {
            let tryTimes = 0;
            const timer = setInterval(() => {
              tryTimes++;
              if (tryTimes > 25) {
                clearInterval(timer);
                setStep(3);
                setLoading(false);
              }
              post(
                GET_INSPECTION_FILE_OCR,
                {
                  inspectionId: inspectionId,
                  fileId:
                    fileId && fileId !== ''
                      ? fileId
                      : CardCaptureStepEnum.vin === type
                        ? vinObj.file?.fileId || ''
                        : CardCaptureStepEnum.plateNumber === type
                          ? numberPlate.file?.fileId || ''
                          : CardCaptureStepEnum.marineHin === type
                            ? marineHin.file?.fileId || ''
                            : CardCaptureStepEnum.marineRegoNumber === type
                              ? marineRegoNumber.file?.fileId || ''
                              : bank.file?.fileId || ''
                },
                false
              )
                .then((res) => {
                  if (
                    res &&
                    (res.status === 'COMPLETE' || res.status === 'ERROR')
                  ) {
                    clearInterval(timer);
                    if (CardCaptureStepEnum.vin === type) {
                      setVinImage({
                        ...vinObj,
                        file: {
                          ...vinObj.file,
                          fileId: fileId || vinObj.file.fileId
                        },
                        ocrFields: res?.ocrFields,
                        ocrStatus: res.status
                      });
                    } else if (CardCaptureStepEnum.plateNumber === type) {
                      setNumberPlateImage({
                        ...numberPlate,
                        file: {
                          ...numberPlate.file,
                          fileId: fileId || numberPlate.file.fileId
                        },
                        ocrFields: res?.ocrFields,
                        ocrStatus: res.status
                      });
                    } else if (CardCaptureStepEnum.marineHin === type) {
                      setMarineHinImage({
                        ...marineHin,
                        file: {
                          ...marineHin.file,
                          fileId: fileId || marineHin.file.fileId
                        },
                        ocrFields: res?.ocrFields,
                        ocrStatus: res.status
                      });
                    } else if (
                      CardCaptureStepEnum.marineRegoNumber === type
                    ) {
                      setMarineRegoNumberImage({
                        ...marineRegoNumber,
                        file: {
                          ...marineRegoNumber.file,
                          fileId: fileId || marineRegoNumber.file.fileId
                        },
                        ocrFields: res?.ocrFields,
                        ocrStatus: res.status
                      });
                    } else if (DocumentCaptureStepEnum.bank === type) {
                      setBankImage({
                        ...bank,
                        file: {
                          ...bank.file,
                          fileId: fileId || bank.file.fileId
                        },
                        ocrFields: res?.ocrFields,
                        ocrStatus: res.status
                      });
                    }
                    setStep(3);
                    setLoading(false);
                  }
                })
                .catch((e) => {
                  recordAPIError(e, e.response?.data);
                  clearInterval(timer);
                  setStep(3);
                  setLoading(false);
                  console.error(e);
                });
            }, 1000);
          } else {
            if (
              data &&
              (data.status === 'COMPLETE' || data.status === 'ERROR')
            ) {
              if (CardCaptureStepEnum.vin === type) {
                setVinImage({
                  ...vinObj,
                  ocrFields: data?.ocrFields,
                  ocrStatus: data.status
                });
              } else if (CardCaptureStepEnum.plateNumber === type) {
                setNumberPlateImage({
                  ...numberPlate,
                  ocrFields: data?.ocrFields,
                  ocrStatus: data.status
                });
              } else if (CardCaptureStepEnum.marineHin === type) {
                setMarineHinImage({
                  ...marineHin,
                  ocrFields: data?.ocrFields,
                  ocrStatus: data.status
                });
              } else if (CardCaptureStepEnum.marineRegoNumber === type) {
                setMarineRegoNumberImage({
                  ...marineRegoNumber,
                  ocrFields: data?.ocrFields,
                  ocrStatus: data.status
                });
              }
            } else {
              if (CardCaptureStepEnum.vin === type) {
                setVinImage({
                  ...vinObj,
                  ocrFields: [],
                  ocrStatus: data.status
                });
              } else if (CardCaptureStepEnum.plateNumber === type) {
                setNumberPlateImage({
                  ...numberPlate,
                  ocrFields: [],
                  ocrStatus: data.status
                });
              } else if (CardCaptureStepEnum.marineHin === type) {
                setMarineHinImage({
                  ...marineHin,
                  ocrFields: [],
                  ocrStatus: data.status
                });
              } else if (CardCaptureStepEnum.marineRegoNumber === type) {
                setMarineRegoNumberImage({
                  ...marineRegoNumber,
                  ocrFields: [],
                  ocrStatus: data.status
                });
              }
            }
            setStep(3);
            setLoading(false);
          }
        })
        .catch((e) => {
          recordAPIError(e, e.response?.data);
          setStep(3);
          setLoading(false);
          console.error(e);
        });
      }, uploadMetricsRef.current);
    } else {
      setLoading(true);
      if (type === CardCaptureStepEnum.odometer) {
        setOdometer({ ...odometer, isDone: true });
      } else if (type === CardCaptureStepEnum.operatingHours) {
        setOperatingHours({ ...operatingHours, isDone: true });
      } else if (type === CardCaptureStepEnum.assetFront) {
        setVehicleImageFront({ ...vehicleFront, isDone: true });
      } else if (type === CardCaptureStepEnum.assetLeft) {
        setVehicleImagePassenger({ ...vehiclePassenger, isDone: true });
      } else if (type === CardCaptureStepEnum.assetRight) {
        setVehicleImageDriver({ ...vehicleDriver, isDone: true });
      } else if (type === CardCaptureStepEnum.assetBack) {
        setVehicleImageBack({ ...vehicleBack, isDone: true });
      } else if (type === CardCaptureStepEnum.internalFront) {
        setVehicleImageFrontSeats({ ...vehicleFrontSeats, isDone: true });
      } else if (type === CardCaptureStepEnum.internalBack) {
        setVehicleImageBackSeats({ ...vehicleBackSeats, isDone: true });
      } else if (type === CardCaptureStepEnum.internalBoot) {
        setVehicleImageBoot({ ...vehicleBoot, isDone: true });
      } else if (type === CardCaptureStepEnum.internalLivingArea) {
        setVehicleImageLivingArea({ ...vehicleLivingArea, isDone: true });
      } else if (type === CardCaptureStepEnum.internalKitchen) {
        setVehicleImageKitchen({ ...vehicleKitchen, isDone: true });
      } else if (type === CardCaptureStepEnum.internalSleepingArea) {
        setVehicleImageSleepingArea({ ...vehicleSleepingArea, isDone: true });
      } else {
        const vehicleKey = getVehiclePhotoStepKey(type, vehicle.vehiclePosition);
        if (vehicleKey) {
          const latest = useSellerStore.getState();
          const vehicleSetters = {
            vehicleFront: setVehicleImageFront,
            vehicleDriver: setVehicleImageDriver,
            vehiclePassenger: setVehicleImagePassenger,
            vehicleBack: setVehicleImageBack
          };
          vehicleSetters[vehicleKey]({ ...latest[vehicleKey], isDone: true });
          const afterVehicle = useSellerStore.getState();
          if (
            areAllVehicleAssetPhotosDone(
              afterVehicle.documentTypes ?? [],
              afterVehicle
            )
          ) {
            setVehicle({ ...afterVehicle.vehicle, isDone: true });
          }
        }
        const marineKey = getMarinePhotoStepKey(type, vehicle.vehiclePosition);
        if (marineKey) {
          const latest = useSellerStore.getState();
          const marineSetters = {
            marineFront: setMarineImageFront,
            marineRight: setMarineImageRight,
            marineLeft: setMarineImageLeft,
            marineBack: setMarineImageBack
          };
          marineSetters[marineKey]({ ...latest[marineKey], isDone: true });
        }
      }
      uploadFile(type, image, () => {
        setStep(4);
        setLoading(false);
      }, uploadMetricsRef.current);
    }
  };

  const captureImageBtn = useRef<HTMLInputElement>(null);

  const uploadFile = (
    type: any,
    image: any,
    cb: (fileId?: any) => void,
    uploadMetrics?: ProcessFileMetrics
  ) => {
    const getMarineDocumentType = (position: number) => {
      switch (position) {
        case 1:
          return 'MARINE_FRONT';
        case 2:
          return 'MARINE_RIGHT';
        case 3:
          return 'MARINE_LEFT';
        case 4:
          return 'MARINE_BACK';
        default:
          return 'MARINE_FRONT';
      }
    };

    const documentType: string =
      type === CardCaptureStepEnum.vehiclePhoto
        ? VehiclePhotosEnum[vehicle.vehiclePosition - 1]
        : type === CardCaptureStepEnum.marinePhoto
          ? getMarineDocumentType(vehicle.vehiclePosition)
          : type;
    setLoading(true);

    async function addFile() {
      const geolocationManager = GeolocationManager.getInstance();
      let locationData = null;
      const locationStartTime = performance.now();
      try {
        locationData = await geolocationManager.getLocationForUpload(inspectionConfig?.locationSharing);
      } catch (err) {
        console.error('Failed to get location for upload:', err);
      }
      const locationWaitTimeMs = Math.round(performance.now() - locationStartTime);

      // Calculate image size from base64 string
      const base64Data = image.split('base64,').length > 1 ? image.split('base64,')[1] : image;
      const finalFileSize = uploadMetrics?.finalFileSize ?? Math.round((base64Data.length * 3) / 4);
      const originalFileSize = uploadMetrics?.originalFileSize ?? finalFileSize;
      const compressionTimeMs = uploadMetrics?.compressionTimeMs ?? 0;

      const params = {
        comment: '',
        dataBase64: base64Data,
        documentType: documentType,
        extension: getExtensionFromBase64(image),
        inspectionId: inspectionId,
        clientAccuracy: locationData?.clientAccuracy,
        clientLatitude: locationData?.clientLatitude,
        clientLongitude: locationData?.clientLongitude
      };

      const apiStartTime = performance.now();
      post(ADD_INSPECTION_FILE, params, false, { timeout: 58000 })
        .then((data) => {
          const apiTimeMs = Math.round(performance.now() - apiStartTime);
          setLoading(false);
          if (data && data.fileId) {
            // Record successful file upload event
            recordFileUploadEvent({
              inspectionId,
              documentType,
              originalFileSize,
              finalFileSize,
              locationWaitTimeMs,
              compressionTimeMs,
              apiTimeMs,
              success: true
            });

            if (documentType === CardCaptureStepEnum.vin) {
              setVinFile({ fileId: data.fileId });
            } else if (documentType === CardCaptureStepEnum.plateNumber) {
              setPlateNumberFile({ fileId: data.fileId });
            } else if (documentType === CardCaptureStepEnum.marineHin) {
              setVinFile({ fileId: data.fileId });
            } else if (documentType === CardCaptureStepEnum.marineRegoNumber) {
              setPlateNumberFile({ fileId: data.fileId });
            } else if (documentType === DocumentCaptureStepEnum.bank) {
              setBankFile({ fileId: data.fileId });
            }
            cb(data.fileId);
          } else {
            recordFileUploadEvent({
              inspectionId,
              documentType,
              originalFileSize,
              finalFileSize,
              locationWaitTimeMs,
              compressionTimeMs,
              apiTimeMs: Math.round(performance.now() - apiStartTime),
              success: false,
              error: 'No fileId in response'
            });
            alert('Failed to upload file');
          }
        })
        .catch((e) => {
          const apiTimeMs = Math.round(performance.now() - apiStartTime);
          recordAPIError(e, e.response?.data);

          // Record failed file upload event
          recordFileUploadEvent({
            inspectionId,
            documentType,
            originalFileSize,
            finalFileSize,
            locationWaitTimeMs,
            compressionTimeMs,
            apiTimeMs,
            success: false,
            error: e.message || 'API call failed'
          });

          console.error(e);
          if (
            e.code === 'ECONNABORTED' ||
            e.response?.status === 408 ||
            e.message.includes('Network Error')
          ) {
            setShowErrorDialog(true);
          } else {
            alert('Failed to upload file, Please login again.');
          }
          setLoading(false);
        });
    }
    addFile();
  };
  const [image, setImage] = useState<string>(initialImage);

  const [trigger2, setTrigger2] = useState(false);

  const handleTakePicture = async (targetRef: HTMLInputElement) => {
    if (targetRef) {
      try {
        if (
          window.navigator.permissions ||
          inspectionConfig?.locationSharing === 'SILENT'
        ) {
          const result = geolocationResult;
          if (
            result.state === 'granted' ||
            inspectionConfig?.locationSharing === 'SILENT' ||
            (result.state === 'denied' &&
              inspectionConfig?.locationSharing === 'WARNING_PROMPT')
          ) {
            setShowLocationPrompt(false);
            targetRef.click();
            return;
          }
        }

        setTrigger2(true);
        setShowLocationPrompt(true);
      } catch (error) {
        recordError({ error, errorType: 'LOCATION_PERMISSION_ERROR' });
        console.error('Error checking location permission:', error);
        setTrigger2(true);
        setShowLocationPrompt(true);
      }
    }
  };
  useEffect(() => {
    if (trigger2 && locationData) {
      setShowLocationPrompt(false);
      setTimeout(() => captureImageBtn.current?.click(), 300);
      setTrigger2(false);
    }
  }, [trigger2, locationData]);
  const handleCaptureNewImage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const validationConfig =
        type === DocumentCaptureStepEnum.bank
          ? FILE_VALIDATION_CONFIGS.BANK_STATEMENT
          : FILE_VALIDATION_CONFIGS.IMAGES_ONLY;

      const validationResult = validateFile(file, validationConfig);

      if (!validationResult.isValid) {
        setFileErrorMessage(
          validationResult.message || 'File validation failed'
        );
        setShowFileTypeError(true);
        event.target.value = '';
        return;
      }

      setLoading(true);
      await processFile(file, async (imageDataUrl, compressedFile, metrics) => {
        // Store metrics for upload tracking
        if (metrics) {
          uploadMetricsRef.current = metrics;
        }
        setBankImage({
          ...bank,
          fileName: compressedFile.name
        });

        const geolocationManager = GeolocationManager.getInstance();
        geolocationManager.startBackgroundLocationRequest();

        setTimeout(() => {
          setLoading(false);
          setImage(imageDataUrl);
          setLoadImage(imageDataUrl);
        }, 1000);
      });
    } else {
      console.log('No file selected.');
    }
  };
  const handleTakeNewPicture = async () => {
    if (
      type === DocumentCaptureStepEnum.bank ||
      type === CardCaptureStepEnum.vehiclePhoto ||
      type === CardCaptureStepEnum.marinePhoto ||
      type === CardCaptureStepEnum.vin ||
      type === CardCaptureStepEnum.plateNumber ||
      type === CardCaptureStepEnum.odometer ||
      type === CardCaptureStepEnum.operatingHours ||
      type === CardCaptureStepEnum.assetFront ||
      type === CardCaptureStepEnum.assetLeft ||
      type === CardCaptureStepEnum.assetRight ||
      type === CardCaptureStepEnum.assetBack ||
      type === CardCaptureStepEnum.internalFront ||
      type === CardCaptureStepEnum.internalBack ||
      type === CardCaptureStepEnum.internalBoot ||
      type === CardCaptureStepEnum.internalLivingArea ||
      type === CardCaptureStepEnum.internalKitchen ||
      type === CardCaptureStepEnum.internalSleepingArea
    ) {
      handleTakePicture(captureImageBtn.current as HTMLInputElement);
    } else {
      setStep(1);
    }
  };
  if (isLoading) return <LoadingComponent color={primaryColor} />;

  const resultButtonTestId = (
    action: 'continue' | 'change-document' | 'confirm' | 'retake'
  ) => `seller-capture-result-${type.toLowerCase()}-${action}-button`;

  const DocumentStart = () => {
    return (
      <div className="flex flex-col gap-8 h-[calc(100vh-150px)] justify-between w-full">
        <div className="gap-3 flex flex-col">
          <h2
            style={{
              fontFamily: 'Inter',
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '24px',
              textAlign: 'left',
              color: 'text-grey-900'
            }}>
            Document upload
          </h2>
          <p
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              textAlign: 'left',
              color: 'text-grey-600'
            }}>
            Your document has successfully uploaded.
          </p>
        </div>
        <div className="w-full h-full">
          {type === DocumentCaptureStepEnum.bank && (
            <div className="flex flex-row gap-2 justify-between items-start">
              <span className="flex flex-row gap-2 items-center">
                <DocumentSellerIcon color={primaryColor} />{' '}
                {bank.file &&
                  (() => {
                    const fileName = bank.fileName?.toLowerCase() || '';
                    const isImage =
                      fileName.endsWith('.jpg') ||
                      fileName.endsWith('.jpeg') ||
                      fileName.endsWith('.png');
                    if (isImage && typeof image === 'string') {
                      return (
                        <img
                          src={image}
                          alt={bank.fileName}
                          style={{ maxHeight: 160, maxWidth: 120, borderRadius: 6, marginRight: 8 }}
                        />
                      );
                    }
                    return null;
                  })()}
                <span>{bank.fileName}</span>
              </span>

              <PrimaryCheck />
            </div>
          )}
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={async (event: React.ChangeEvent<HTMLInputElement>) => await handleFileChange(event)}
          style={{ display: 'none' }}
        />
        <div className="fixed lg:relative lg:w-full lg:justify-end bottom-0 right-0 w-full px-4 py-6 bg-grey-50 flex flex-col gap-4 tablet-portrait:w-[361px] tablet-portrait:justify-center tablet-portrait:right-auto tablet-portrait:px-0">
          <Button
            data-testid={resultButtonTestId('continue')}
            className={clsx(
              'font-semibold w-auto normal-case rounded text-base shadow-none py-[10px] px-[18px]',
              primaryColor && `bg-[${primaryColor}]`
            )}
            style={{ backgroundColor: primaryColor }}
            onClick={async () => await handleUpload()}>
            Continue
          </Button>
          <Button
            data-testid={resultButtonTestId('change-document')}
            variant="text"
            className="text-grey-600 normal-case font-semibold rounded text-base hover:bg-blue-500/0 active:bg-blue-500/0 focus:bg-blue-500/0 py-[10px] px-[18px]"
            onClick={async () => await uploadTakeNewPicture()}>
            Change uploaded document
          </Button>
        </div>
      </div>
    );
  };
  return (
    <>
      {isUpload ? (
        <DocumentStart />
      ) : (
        <div
          className="flex flex-col gap-8 justify-start overflow-y-auto no-scrollbar w-full pb-[140px]">
          <div className="gap-3 flex flex-col">
            <h2
              style={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px',
                textAlign: 'left',
                color: 'text-grey-900'
              }}>
              Check{' '}
              {type === CardCaptureStepEnum.vehiclePhoto
                ? 'visibility'
                : 'readability'}
            </h2>
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                textAlign: 'left',
                color: 'text-grey-600'
              }}>
              {(() => {
                const msg = getCaptureGuidanceMessage(type, assetType, vehicle.vehiclePosition);
                if (msg.plainText) return msg.plainText;
                if (msg.boldSubject) return (
                  <>
                    Make sure the{' '}
                    <span className="font-bold">{msg.boldSubject}</span>{' '}
                    is clearly visible, with no obstructions, blur or glare.
                  </>
                );
                return null;
              })()}
            </p>
          </div>
          <div className="flex justify-center w-full h-auto">
            {type !== DocumentCaptureStepEnum.bank &&
              type !== CardCaptureStepEnum.vehiclePhoto &&
              type !== CardCaptureStepEnum.marinePhoto &&
              type !== CardCaptureStepEnum.vin &&
              type !== CardCaptureStepEnum.plateNumber &&
              type !== CardCaptureStepEnum.odometer &&
              type !== CardCaptureStepEnum.operatingHours &&
              type !== CardCaptureStepEnum.assetFront &&
              type !== CardCaptureStepEnum.assetLeft &&
              type !== CardCaptureStepEnum.assetRight &&
              type !== CardCaptureStepEnum.assetBack &&
              type !== CardCaptureStepEnum.internalFront &&
              type !== CardCaptureStepEnum.internalBack &&
              type !== CardCaptureStepEnum.internalBoot &&
              type !== CardCaptureStepEnum.internalLivingArea &&
              type !== CardCaptureStepEnum.internalKitchen &&
              type !== CardCaptureStepEnum.internalSleepingArea && (
                <img
                  src={
                    image.split('base64,').length > 1
                      ? image
                      : `data:image/jpeg;base64,${image}`
                  }
                  style={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: window.innerHeight < 698 ? '400px' : '420px',
                    objectFit: 'cover',
                    borderColor: primaryColor
                  }}
                  alt="Captured Image"
                  className={clsx(
                    'border-2  rounded-lg',
                    window.innerHeight > 698 ? 'mt-[25%] mb-[25%]' : 'mt-[10%] mb-[10%]'
                  )}
                />
              )}
            {(type === DocumentCaptureStepEnum.bank ||
              type === CardCaptureStepEnum.vehiclePhoto ||
              type === CardCaptureStepEnum.marinePhoto ||
              type === CardCaptureStepEnum.vin ||
              type === CardCaptureStepEnum.plateNumber ||
              type === CardCaptureStepEnum.odometer ||
              type === CardCaptureStepEnum.operatingHours ||
              type === CardCaptureStepEnum.assetFront ||
              type === CardCaptureStepEnum.assetLeft ||
              type === CardCaptureStepEnum.assetRight ||
              type === CardCaptureStepEnum.assetBack ||
              type === CardCaptureStepEnum.internalFront ||
              type === CardCaptureStepEnum.internalBack ||
              type === CardCaptureStepEnum.internalBoot ||
              type === CardCaptureStepEnum.internalLivingArea ||
              type === CardCaptureStepEnum.internalKitchen ||
              type === CardCaptureStepEnum.internalSleepingArea) && (
              <>
                <img
                  src={
                    image.split('base64,').length > 1
                      ? image
                      : `data:image/jpeg;base64,${image}`
                  }
                  style={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: window.innerHeight < 698 ? '400px' : '420px',
                    borderColor: primaryColor,
                    objectFit: 'cover'
                  }}
                  alt="Captured Image"
                  className="border-2 rounded-lg"
                />
                <input
                  ref={captureImageBtn}
                  type="file"
                  capture="environment"
                  accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                  style={{ display: 'none' }}
                  onChange={handleCaptureNewImage}
                />
              </>
            )}
          </div>
          <div className="fixed lg:relative lg:w-full lg:justify-end bottom-0 right-0 w-full px-4 py-6 bg-grey-50 flex flex-col gap-4 tablet-portrait:w-[361px] tablet-portrait:justify-center tablet-portrait:right-auto tablet-portrait:px-0">
            <Button
              data-testid={resultButtonTestId('confirm')}
              className={clsx(
                'font-semibold w-auto normal-case rounded text-base shadow-none py-[10px] px-[18px]',
                primaryColor && `bg-[${primaryColor}]`
              )}
              style={{ backgroundColor: primaryColor }}
              onClick={handleUpload}>
              {[
                CardCaptureStepEnum.vin,
                CardCaptureStepEnum.plateNumber,
                DocumentCaptureStepEnum.bank
              ].includes(type)
                ? 'Image is readable'
                : 'Image is clearly visible'}
            </Button>
            <Button
              data-testid={resultButtonTestId('retake')}
              variant="text"
              className="text-grey-600 normal-case font-semibold rounded text-base shadow-none hover:bg-blue-500/0 py-[10px] px-[18px]"
              onClick={() => {
                handleTakeNewPicture();
              }}>
              Take a new picture
            </Button>
          </div>
        </div>
      )}
      <UploadErrorModal
        open={showErrorDialog}
        onClose={handleRetry}
        primaryColor={primaryColor}
      />

      <FileTypeErrorModal
        open={showFileTypeError}
        onClose={() => setShowFileTypeError(false)}
        primaryColor={primaryColor}
        message={fileErrorMessage}
      />
    </>
  );
};

export default CaptureResult;
